/*
 * Copyright 2022 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useApi } from '@mainstage/core-plugin-api';
import { JsonObject } from '@mainstage/types';
import { useState } from 'react';
import {
  WbDockableGroup,
  WbDockablePanel,
  WbDockableResizeHandle,
  customAlertApiRef,
} from '@agilelab/plugin-wb-platform';
import { makeStyles, useTheme } from '@material-ui/core';
import { CustomError } from '@agilelab/plugin-wb-platform-common';
import { FieldExtensionOptions } from '../../extensions/types';
import type { LayoutOptions } from '../../layouts';
import { TemplateDirectoryAccess } from '../../lib/filesystem';
import { DirectoryEditorProvider } from './DirectoryEditorContext';
import { DryRunProvider } from './DryRunContext';
import { DryRunResults } from './DryRunResults';
import {
  TemplateEditorBrowser,
  TemplateEditorBrowserActions,
} from './TemplateEditorBrowser';
import { TemplateEditorForm } from './TemplateEditorForm';
import { TemplateEditorTextArea } from './TemplateEditorTextArea';
import { LiveFormParametersPanel } from './FormContextInspector';

const useStyles = makeStyles(
  theme => ({
    root: {
      gridArea: 'pageContent',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    horizontalGroup: {
      flex: 1,
      minHeight: 0,
    },
    whitePanel: {
      background: theme.palette.background.paper,
    },
    verticalGroup: {
      height: '100%',
    },
  }),
  { name: 'TemplateEditor' },
);

export const TemplateEditor = (props: {
  directory: TemplateDirectoryAccess;
  fieldExtensions?: FieldExtensionOptions<any, any>[];
  layouts?: LayoutOptions[];
  onClose?: () => void;
}) => {
  const alertApi = useApi(customAlertApiRef);
  const classes = useStyles();
  const theme = useTheme();

  const [errorText, setErrorText] = useState<string>();
  const [formData, setFormData] = useState<JsonObject>({});
  const [clearKey, setClearKey] = useState(0);

  const handleClear = () => {
    setFormData({});
    setClearKey(k => k + 1);
  };

  return (
    <DirectoryEditorProvider
      directory={props.directory}
      onError={error => {
        alertApi.post({
          error: new CustomError(
            'Error while loading template directory',
            error.message,
          ),
          severity: 'error',
        });
        props.onClose?.();
      }}
    >
      <DryRunProvider>
        <main className={classes.root}>
          <WbDockableGroup
            direction="horizontal"
            className={classes.horizontalGroup}
          >
            <WbDockablePanel
              defaultSize={20}
              minSize={10}
              primary
              title="Browser"
              className={classes.whitePanel}
              cardHeaderStyles={{
                background: theme.palette.background.default,
              }}
              headerActions={
                <TemplateEditorBrowserActions onClose={props.onClose} />
              }
            >
              <WbDockableGroup
                direction="vertical"
                className={classes.verticalGroup}
              >
                <WbDockablePanel
                  defaultSize={55}
                  minSize={20}
                  primary
                  noHeader
                  title="Files"
                  cardHeaderStyles={{
                    background: theme.palette.background.default,
                  }}
                  headerBorderBottom
                >
                  <TemplateEditorBrowser />
                </WbDockablePanel>
                <WbDockableResizeHandle direction="vertical" />
                <LiveFormParametersPanel
                  defaultSize={45}
                  minSize={20}
                  className={classes.whitePanel}
                  cardHeaderStyles={{
                    background: theme.palette.background.default,
                    paddingLeft: theme.spacing(1),
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                  formContext={formData}
                  onClear={handleClear}
                />
              </WbDockableGroup>
            </WbDockablePanel>
            <WbDockableResizeHandle direction="horizontal" />
            <WbDockablePanel
              defaultSize={50}
              minSize={20}
              collapsible={false}
              noHeader
              title="Editor"
            >
              <TemplateEditorTextArea.DirectoryEditor errorText={errorText} />
            </WbDockablePanel>
            <WbDockableResizeHandle direction="horizontal" />
            <WbDockablePanel
              defaultSize={30}
              minSize={15}
              collapsible={false}
              noHeader
              title="Preview"
            >
              <TemplateEditorForm.DirectoryEditorDryRun
                key={clearKey}
                data={formData}
                onUpdate={setFormData}
                setErrorText={setErrorText}
                fieldExtensions={props.fieldExtensions}
                layouts={props.layouts}
              />
            </WbDockablePanel>
          </WbDockableGroup>
          <DryRunResults />
        </main>
      </DryRunProvider>
    </DirectoryEditorProvider>
  );
};
